import { isValidPhoneNumber } from 'libphonenumber-js';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import * as Yup from 'yup';

import { Contact, ContactStatus, CreateContactInput, UpdateContactInput } from '../types';
export const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const INVALID_PHONE_NUMBER_FORMAT = 'The format of this phone number is not valid';
const CountryCodeSchema = /* @__PURE__ */ Yup.string().min(1);
const PhoneNumberSchema = /* @__PURE__ */ Yup.string().test(
  'validPhoneNumber',
  INVALID_PHONE_NUMBER_FORMAT,
  function (value) {
    if (isEmpty(value)) return true;
    const { countryCode } = this.parent;
    return isValidPhoneNumber(`${countryCode}${value}`);
  }
);

const PhoneNumbersSchema = /* @__PURE__ */ Yup.array()
  .of(
    Yup.object({
      countryCode: CountryCodeSchema.required(),
      isPrimary: Yup.boolean().required(),
      number: PhoneNumberSchema,
    })
  )
  .nullable()
  .notRequired();

const EmailSchema = /* @__PURE__ */ Yup.string().email().matches(EMAIL_REGEX).nullable().notRequired();

const EmailObjectSchema = (contactEmail?: string | null) =>
  Yup.object({
    label: Yup.string().nullable(),
    address: Yup.string()
      .required('Email address is required')
      .email('Invalid email format')
      .matches(EMAIL_REGEX, 'Invalid email format'),
    isPrimary: Yup.boolean().required(),
  }).test('primary-matches-contact', 'Primary email must match contact.email', (email) => {
    if (!email) return true;
    return email.isPrimary ? email.address === contactEmail : true;
  });

const EmailsArraySchema = (contactEmail?: string | null) =>
  Yup.array()
    .of(EmailObjectSchema(contactEmail))
    .required('Emails array is required')
    .test('only-one-primary', 'There can only be one or zero primary email', (emails) =>
      isEmpty(emails) ? true : size(emails?.filter((e) => e.isPrimary)) <= 1
    )
    .notRequired();

const isInputContactValid = (contact: CreateContactInput | UpdateContactInput | Partial<Contact>): boolean => {
  try {
    EmailSchema.validateSync(contact.email);
    EmailsArraySchema(contact.email).validateSync(contact.emails);
    PhoneNumbersSchema.validateSync(contact.phoneNumbers);
  } catch {
    return false;
  }
  return true;
};

export const getContactStatus = (
  contact: CreateContactInput | UpdateContactInput | Partial<Contact>
): ContactStatus | undefined => {
  if ([ContactStatus.ARCHIVED, ContactStatus.DRAFT_OCR].includes(contact.status as ContactStatus)) {
    return contact.status as ContactStatus;
  }
  if (isEmpty(contact.email) && isEmpty(contact.phoneNumbers)) {
    return;
  }
  return isInputContactValid(contact) ? ContactStatus.VALID : ContactStatus.ERROR;
};
